import { FormEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import AuthShell from '../../components/auth/AuthShell';
import { useAuth } from '../../contexts/AuthContext';
import { PASSWORD_RESET_TOKEN_VALID_QUERY } from '../../graphql/queries';
import { RESET_PASSWORD_MUTATION } from '../../graphql/mutations';
import styles from './ResetPassword.module.css';
import Icon from '../../components/common/Icon';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const { isAuthenticated, logout } = useAuth();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const { data, loading: checking, error: checkError } = useQuery(PASSWORD_RESET_TOKEN_VALID_QUERY, {
    variables: { token },
    skip: !token,
    fetchPolicy: 'network-only',
  });

  const [resetPassword, { loading: saving }] = useMutation(RESET_PASSWORD_MUTATION, {
    onCompleted: () => {
      // The reset signed every session out, this browser's included.
      if (isAuthenticated) logout();
      setDone(true);
    },
    onError: (err) => setError(err.message),
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 8) { setError('The password must be at least 8 characters.'); return; }
    if (password !== repeat) { setError('The two passwords are not the same.'); return; }
    setError('');
    resetPassword({ variables: { token, password } });
  };

  const invalid = !token || data?.passwordResetTokenValid === false;

  if (done) {
    return (
      <AuthShell title="Password changed">
        <div className={`${styles.notice} ${styles.noticeOk}`} role="status">
          <Icon name="check_circle" />
          <p>Your new password is set. Every device that was signed in has been signed out.</p>
        </div>
        <Link to="/login" className="mt-6 w-full py-3.5 btn-primary rounded-lg">Log in</Link>
      </AuthShell>
    );
  }

  if (checking) {
    return (
      <AuthShell title="Choose a new password">
        <p className="text-sm text-on-surface-variant">Checking your link…</p>
      </AuthShell>
    );
  }

  if (invalid || checkError) {
    return (
      <AuthShell title="This link does not work">
        <div className={`${styles.notice} ${styles.noticeBad}`} role="alert">
          <Icon name="link_off" />
          <p>
            {checkError
              ? `Could not check the link: ${checkError.message}`
              : 'The reset link is invalid, was already used, or is older than 2 hours. Ask for a new one.'}
          </p>
        </div>
        <div className="mt-6 flex justify-between">
          <Link to="/forgot-password" className={styles.link}>Send a new link</Link>
          <Link to="/login" className={styles.link}>Back to log in</Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Choose a new password" subtitle="At least 8 characters. This signs you out everywhere else.">
      {error && (
        <div className="mb-4 p-3 bg-error-container rounded-lg flex items-center gap-2 text-sm text-on-error-container" role="alert">
          <Icon name="error" className="text-base" />
          {error}
        </div>
      )}
      <form className="space-y-5" onSubmit={handleSubmit}>
        <div className="space-y-2">
          <label className="block text-xs font-bold uppercase tracking-wider text-on-surface-variant font-label" htmlFor="new-password">
            New password
          </label>
          <div className={styles.inputWrapper}>
            <input
              className={styles.input}
              id="new-password"
              type={show ? 'text' : 'password'}
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button type="button" className={styles.passwordToggle} onClick={() => setShow(!show)} aria-label={show ? 'Hide password' : 'Show password'}>
              <Icon name={show ? 'visibility_off' : 'visibility'} className="text-lg" />
            </button>
          </div>
        </div>
        <div className="space-y-2">
          <label className="block text-xs font-bold uppercase tracking-wider text-on-surface-variant font-label" htmlFor="repeat-password">
            Repeat new password
          </label>
          <input
            className={styles.input}
            id="repeat-password"
            type={show ? 'text' : 'password'}
            autoComplete="new-password"
            required
            value={repeat}
            onChange={(e) => setRepeat(e.target.value)}
          />
        </div>
        <button
          type="submit"
          disabled={saving}
          className="w-full py-3.5 btn-primary rounded-lg shadow-md shadow-primary/20 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving…' : 'Set new password'}
        </button>
      </form>
      <div className="mt-6 text-center">
        <Link to="/login" className={styles.link}>Back to log in</Link>
      </div>
    </AuthShell>
  );
};

export default ResetPassword;
